
import React, { createContext, useContext, useEffect, useState } from "react";
import { auth } from "../../firebaseClient";
import { getRoleByEmail, UserRole, isReadOnlyUser } from "../config/roles";

export type { UserRole };

export type AppUser = {
  id: string;
  name: string;
  email: string;
  role: UserRole; 
  displayName: string;
  isReadOnly: boolean; 
};

type AuthContextValue = {
  user: AppUser | null;
  isAuthenticated: boolean;
  isSealUser: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  debugViewDepartmentId: string | null;
  setDebugViewDepartmentId: (id: string | null) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const LG_APP_DOMAIN = "lg.app";
const INTERNAL_ALLOWLIST = new Set([
  "kc","ad","lisa","irene","king","ken",
  "gm","fa","ap","it","lg","sl1","sl2","li","pt","qc"
]);

function normalizeLoginEmail(input: string) {
  const v = (input || "").trim();
  if (!v) return "";
  if (v.includes("@")) return v.toLowerCase();
  // 支援只輸入帳號代碼（kc / gm / sl1 ...）
  return `${v.toLowerCase()}@${LG_APP_DOMAIN}`;
}

function isInternalWhitelisted(email: string) {
  const parts = (email || "").toLowerCase().split("@");
  if (parts.length !== 2) return false;
  const [local, domain] = parts;
  return domain === LG_APP_DOMAIN && INTERNAL_ALLOWLIST.has(local);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [debugViewDepartmentId, setDebugViewDepartmentId] = useState<string | null>(null);

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      if (u) {
          setUser({
              id: u.uid,
              name: u.displayName || u.email?.split('@')[0] || 'User',
              email: u.email || '',
              role: getRoleByEmail(u.email),
              displayName: u.displayName || u.email?.split('@')[0] || 'User',
              isReadOnly: isReadOnlyUser(u.email)
          });
      } else {
          setUser(null);
      }
    });
    return () => unsub();
  }, []);

  async function login(emailInput: string, password: string) {
    const email = normalizeLoginEmail(emailInput);

    try {
      await auth.signInWithEmailAndPassword(email, password);
    } catch (e: any) {
      const code = e?.code || "";
      // 若是白名單帳號且 Firebase 找不到使用者：自動建立後再登入（一次性補齊）
      if (code === "auth/user-not-found" && isInternalWhitelisted(email)) {
        await auth.createUserWithEmailAndPassword(email, password);
        // createUser automatically signs in upon success
        return;
      }
      throw e;
    }
  }

  const logout = async () => { await auth.signOut(); };

  return (
    <AuthContext.Provider value={{ 
        user, 
        isAuthenticated: !!user, 
        isSealUser: false, 
        login, 
        logout, 
        debugViewDepartmentId, 
        setDebugViewDepartmentId 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
